import { MutableRefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import { AddOnTextInput, Badge, Button, IconButton, Modal, Typography } from "@/components/atoms";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setFixedPrice } from "@/redux/slices/clientBooking/clientBookingSlice";
import { selectFixedPrice } from "@/redux/slices/clientBooking/selectors";
import { clsx, formatAud } from "@/utils";

const BACKEND_MIN_FIXED_PRICE = 5;

type Mode = { kind: "idle" } | { kind: "editing"; draft: string } | { kind: "confirming"; draft: string } | { kind: "resetting" };

interface TotalPriceOverrideProps {
  total?: number;
  preOverrideTotal?: number;
  /**
   * Whether the trigger takes focus on its next idle render. Owned by the parent because confirming
   * re-quotes, and the loading skeleton unmounts this component meanwhile.
   */
  returnFocusRef?: MutableRefObject<boolean>;
}

const actionButtonClass = "h-8 w-8 shrink-0 text-neutral-dark-gray hover:text-primary";

export const TotalPriceOverride = ({ total, preOverrideTotal, returnFocusRef }: TotalPriceOverrideProps) => {
  const dispatch = useAppDispatch();
  const fixedPrice = useAppSelector(selectFixedPrice);
  const overridden = !!fixedPrice;

  const [mode, setMode] = useState<Mode>({ kind: "idle" });
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const localReturnFocusRef = useRef(false);
  const returnFocus = returnFocusRef ?? localReturnFocusRef;
  const isEditing = mode.kind === "editing";
  const isIdle = mode.kind === "idle";

  useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);

  // Hand focus back only when closing dropped it (on the body, or still in the closing dialog), never
  // from a field the user moved to. A closing Dialog keeps the page `inert` through its leave transition,
  // where focus() is a no-op, so wait for every inert ancestor to go. Until then the flag outlives an
  // unmount, for the re-quote remount.
  useEffect(() => {
    const trigger = triggerRef.current;
    if (!isIdle || !returnFocus.current || !trigger) return;
    const settle = () => {
      returnFocus.current = false;
      const active = document.activeElement;
      const dialog = active?.closest('[role="dialog"]');
      if (!active || active === document.body || (dialog && !dialog.contains(trigger))) trigger.focus();
    };
    if (!trigger.closest("[inert]")) return settle();
    const observer = new MutationObserver(() => {
      if (trigger.closest("[inert]")) return;
      observer.disconnect();
      settle();
    });
    observer.observe(document.body, { subtree: true, attributes: true, attributeFilter: ["inert"] });
    return () => observer.disconnect();
  }, [isIdle, returnFocus]);

  // A layout cleanup runs before React detaches the DOM, while the trigger can still hold focus. Carry
  // that focus to the next mount, so it survives the re-quote skeleton and the Set price site swap.
  useLayoutEffect(
    () => () => {
      if (triggerRef.current && triggerRef.current === document.activeElement) returnFocus.current = true;
    },
    [returnFocus]
  );

  const draft = mode.kind === "editing" || mode.kind === "confirming" ? mode.draft : null;
  const parsed = Number(draft);
  const entered = draft != null && draft.trim() !== "" && !Number.isNaN(parsed);
  const draftValid = entered && parsed >= BACKEND_MIN_FIXED_PRICE;
  const belowMinimum = entered && parsed < BACKEND_MIN_FIXED_PRICE;

  const startEditing = () => setMode({ kind: "editing", draft: (total ?? fixedPrice)?.toFixed(2) ?? "" });
  const closeToIdle = () => {
    returnFocus.current = true;
    setMode({ kind: "idle" });
  };
  const backToEditing = () => {
    if (draft != null) setMode({ kind: "editing", draft });
  };
  const requestConfirm = () => {
    if (draft != null && draftValid) setMode({ kind: "confirming", draft });
  };

  const commitOverride = () => {
    dispatch(setFixedPrice(Math.round(parsed * 100) / 100));
    closeToIdle();
  };

  const commitReset = () => {
    dispatch(setFixedPrice(undefined));
    closeToIdle();
  };

  const label = (
    <div className="flex flex-col items-start gap-1">
      <div className="flex h-8 items-center">
        <Typography variant="title">Total</Typography>
      </div>
      {overridden && preOverrideTotal != null && draft == null && (
        <Badge type="info" size="sm" message="Overridden" className="!rounded-full border border-info !bg-white" />
      )}
    </div>
  );

  const value =
    draft != null ? (
      <div className="flex items-center gap-2">
        <AddOnTextInput
          ref={inputRef}
          size="sm"
          className="w-36"
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          startAddOn="AUD $"
          placeholder="0.00"
          hasError={belowMinimum}
          value={draft}
          onChange={(e) => setMode({ kind: "editing", draft: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === "Enter") requestConfirm();
            if (e.key === "Escape") closeToIdle();
          }}
        />
        <IconButton
          variant="custom"
          iconName="TickCircle"
          iconVariant="Linear"
          iconSize="md"
          aria-label="Confirm price override"
          aria-disabled={!draftValid}
          onClick={requestConfirm}
          className={clsx("shrink-0", draftValid ? "text-success" : "cursor-not-allowed text-neutral-mid-gray")}
        />
        <IconButton
          variant="custom"
          iconName="Add"
          iconSize="md"
          aria-label="Cancel price override"
          onClick={closeToIdle}
          className="shrink-0 text-danger [&>svg]:rotate-45"
        />
      </div>
    ) : total == null ? (
      <Button ref={triggerRef} variant="custom" size="sm" className="text-info hover:underline" onClick={startEditing}>
        Set price
      </Button>
    ) : (
      <div className="flex items-start gap-2">
        <div className="flex flex-col items-end">
          <div className="flex h-8 items-center">
            <Typography variant="h2" className="whitespace-nowrap">
              {formatAud(total)}
            </Typography>
          </div>
          {overridden && preOverrideTotal != null && (
            <Typography variant="small-regular" className="text-neutral-dark-gray line-through">
              <span className="sr-only">Original total </span>
              {formatAud(preOverrideTotal)}
            </Typography>
          )}
        </div>
        <IconButton
          ref={triggerRef}
          variant="custom"
          iconName="Edit2"
          iconSize="md"
          aria-label="Edit price override"
          onClick={startEditing}
          className={actionButtonClass}
        />
        {overridden && (
          <IconButton
            variant="custom"
            iconName="Refresh2"
            iconSize="md"
            aria-label="Reset price override"
            onClick={() => setMode({ kind: "resetting" })}
            className={actionButtonClass}
          />
        )}
      </div>
    );

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-start justify-between gap-3">
        {label}
        {value}
      </div>
      {draft != null && (
        <Typography variant="small-regular" className={belowMinimum ? "text-danger" : "text-neutral-dark-gray"}>
          {belowMinimum
            ? `Minimum ${formatAud(BACKEND_MIN_FIXED_PRICE)}`
            : `Enter the new total the client pays (fees included). Minimum ${formatAud(BACKEND_MIN_FIXED_PRICE)}.`}
        </Typography>
      )}

      <Modal
        open={mode.kind === "confirming"}
        onClose={backToEditing}
        variant="web-small"
        className="!max-w-[400px]"
        title="Confirm Price Override"
        description="This will replace the calculated trip price and booking fee with your set fixed amount. The client will receive and be charged this updated price."
        renderButtons={() => (
          <>
            <Button variant="tertiary" size="md" onClick={backToEditing}>
              Cancel
            </Button>
            <Button variant="primary" size="md" onClick={commitOverride}>
              Confirm
            </Button>
          </>
        )}
      />

      <Modal
        open={mode.kind === "resetting"}
        onClose={closeToIdle}
        variant="web-small"
        className="!max-w-[400px]"
        title="Reset Price Override"
        description="This will remove the overridden trip price and revert back to the originally calculated price for this booking."
        renderButtons={() => (
          <>
            <Button variant="tertiary" size="md" onClick={closeToIdle}>
              Cancel
            </Button>
            <Button variant="primary" size="md" onClick={commitReset}>
              Confirm
            </Button>
          </>
        )}
      />
    </div>
  );
};
